import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { articlesApi } from '../../services/articlesApi';
import { photosApi } from '../../services/photosApi';
import { talentsApi } from '../../services/talentsApi';
import { commercesApi } from '../../services/commercesApi';
import { chantiersApi } from '../../services/chantiersApi';
import { elusApi } from '../../services/elusApi';
import { eventsApi } from '../../services/eventsApi';
import { archivesApi } from '../../services/archivesApi';
import { signalementsApi } from '../../services/signalementsApi';
import { seancesApi } from '../../services/seancesApi';
import { sitesApi, circuitsApi } from '../../services/tourismeApi';
import { campagnesApi, cotisationsApi, fmtFCFA } from '../../services/caisseApi';
import { usersApi } from '../../services/usersApi';
import { getToken } from '../auth';
import '../styles/Dashboard.css';

// Wrapper qui swallow les erreurs → un endpoint qui plante ne fait pas tomber le dashboard.
async function safe(p) {
  try { return await p; } catch { return null; }
}
function num(r, key = 'total') { return r && Number.isFinite(r[key]) ? r[key] : 0; }

const Dashboard = () => {
  const token = getToken();
  const [data, setData] = useState({
    articles: 0, photos: 0, talents: 0, commerces: 0,
    chantiers: 0, elus: 0, events: 0, archives: 0,
    signalements: 0, seances: 0, sites: 0, circuits: 0,
    campagnes: 0, cotisations: 0, raised: 0, contributors: 0,
    users: 0, usersActive: 0, usersAdmin: 0,
    recentSignalements: [],
    activeCampagnes: [],
  });
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(new Date());

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const [
      articles, photos, talents, commerces, chantiers, elus, events,
      archives, signalements, seances, sites, circuits,
      campagnes, cotisations, users, usersActive, usersAdmin,
      recentSig, activeCamp,
    ] = await Promise.all([
      safe(articlesApi.adminList(token, { limit: 1 })),
      safe(photosApi.adminList(token, { limit: 1 })),
      safe(talentsApi.adminList(token, { limit: 1 })),
      safe(commercesApi.adminList(token, { limit: 1 })),
      safe(chantiersApi.adminList(token, { limit: 1 })),
      safe(elusApi.adminList(token, { limit: 1 })),
      safe(eventsApi.adminList(token, { limit: 1 })),
      safe(archivesApi.adminList(token, { limit: 1 })),
      safe(signalementsApi.adminList(token, { limit: 1 })),
      safe(seancesApi.adminList(token, { limit: 1 })),
      safe(sitesApi.adminList(token, { limit: 1 })),
      safe(circuitsApi.adminList(token, { limit: 1 })),
      safe(campagnesApi.adminList(token, { limit: 1 })),
      safe(cotisationsApi.adminList(token, { limit: 1 })),
      safe(usersApi.adminList(token, { limit: 1 })),
      safe(usersApi.adminList(token, { active: 'yes', limit: 1 })),
      safe(usersApi.adminList(token, { role: 'admin', limit: 1 })),
      safe(signalementsApi.adminList(token, { status: 'nouveau', limit: 5 })),
      safe(campagnesApi.adminList(token, { status: 'active', limit: 5 })),
    ]);

    setData({
      articles: num(articles),
      photos: num(photos),
      talents: num(talents),
      commerces: num(commerces),
      chantiers: num(chantiers),
      elus: num(elus),
      events: num(events),
      archives: num(archives),
      signalements: num(signalements),
      seances: num(seances),
      sites: num(sites),
      circuits: num(circuits),
      campagnes: num(campagnes),
      cotisations: num(cotisations),
      raised: cotisations?.sumConfirmed?.total || 0,
      contributors: cotisations?.sumConfirmed?.count || 0,
      users: num(users),
      usersActive: num(usersActive),
      usersAdmin: num(usersAdmin),
      recentSignalements: recentSig?.items || [],
      activeCampagnes: activeCamp?.items || [],
    });
    setRefreshedAt(new Date());
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh toutes les 60s (live)
  useEffect(() => {
    if (!token) return;
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [token, load]);

  const totalContent = useMemo(
    () => data.articles + data.photos + data.talents + data.commerces + data.chantiers + data.events + data.archives + data.sites,
    [data]
  );

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Vue d'ensemble</b>
        </div>
        <div className="top-actions" style={{ alignItems: 'center', gap: 14 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.16em', color: 'var(--char-500)' }}>
            {loading ? '↻ chargement…' : `↻ ${refreshedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}
          </span>
          <button className="icon-btn" onClick={load} title="Rafraîchir">↻</button>
        </div>
      </div>

      <div className="content">
        <div className="page-head">
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              Tableau de bord · Auto-refresh 60s
            </span>
            <h1>Cambérène,<br /><span style={{ fontStyle: 'italic' }}>en direct.</span></h1>
            <p className="sub">Toutes les données du village agrégées en temps réel. Cliquez sur une carte pour gérer l'entité correspondante.</p>
          </div>
        </div>

        {/* ====== 4 KPIs vedettes ====== */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
          <KpiCard
            dark
            label="Membres actifs"
            value={data.usersActive}
            sub={`${data.users} comptes total · ${data.usersAdmin} admin`}
            color="#34A853"
            to="/admin/membres"
          />
          <KpiCard
            label="Caisse · collecté"
            value={fmtFCFA(data.raised).replace(' FCFA', '')}
            unit=" FCFA"
            sub={`${data.contributors} contributions · ${data.campagnes} projets`}
            color="#E76F51"
            to="/admin/caisse"
          />
          <KpiCard
            label="Signalements actifs"
            value={data.recentSignalements.length}
            sub={`${data.signalements} au total — à traiter`}
            color={data.recentSignalements.length > 0 ? '#E63946' : 'var(--char-400)'}
            to="/admin/signalements"
          />
          <KpiCard
            label="Contenu total"
            value={totalContent}
            sub="Articles, photos, talents, commerces…"
            color="#1B6985"
            to="/admin/actualites"
          />
        </div>

        {/* ====== Grille mini-KPIs par entité ====== */}
        <div className="page-head" style={{ marginTop: 12, marginBottom: 18 }}>
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              Toutes les entités
            </span>
            <h2 style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 28, margin: '6px 0 0' }}>Compteurs détaillés</h2>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 32 }}>
          <MiniKpi label="Articles"        value={data.articles}    to="/admin/actualites" />
          <MiniKpi label="Photos"          value={data.photos}      to="/admin/galerie" />
          <MiniKpi label="Talents"         value={data.talents}     to="/admin/talents" />
          <MiniKpi label="Commerces"       value={data.commerces}   to="/admin/annuaire" />
          <MiniKpi label="Chantiers"       value={data.chantiers}   to="/admin/chantiers" />
          <MiniKpi label="Élus"            value={data.elus}        to="/admin/conseil" />
          <MiniKpi label="Événements"      value={data.events}      to="/admin/agenda" />
          <MiniKpi label="Archives"        value={data.archives}    to="/admin/archives" />
          <MiniKpi label="Séances"         value={data.seances}     to="/admin/seances" />
          <MiniKpi label="Sites touristiques" value={data.sites}    to="/admin/tourisme" />
          <MiniKpi label="Circuits"        value={data.circuits}    to="/admin/tourisme" />
          <MiniKpi label="Campagnes"       value={data.campagnes}   to="/admin/caisse" />
        </div>

        {/* ====== 2 colonnes : Campagnes actives + Signalements récents ====== */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          <div style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 22, margin: 0 }}>Campagnes actives</h3>
              <Link to="/admin/caisse" className="pill-btn">Gérer</Link>
            </div>
            {data.activeCampagnes.length === 0 && <p style={{ color: 'var(--char-500)', fontStyle: 'italic', fontSize: 13 }}>Aucune campagne active.</p>}
            {data.activeCampagnes.map((c) => (
              <div key={c.id} style={{ paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid var(--line)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <b style={{ fontWeight: 500, fontSize: 14 }}>{c.title}</b>
                  <span style={{ fontFamily: 'var(--serif)', fontSize: 18 }}>{c.progress}%</span>
                </div>
                <div style={{ height: 5, background: 'var(--bone-2)', position: 'relative', marginBottom: 6 }}>
                  <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${c.progress}%`, background: '#E76F51', transition: 'width .8s' }} />
                </div>
                <small style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--char-500)' }}>
                  {fmtFCFA(c.raised)} / {fmtFCFA(c.goal)}
                </small>
              </div>
            ))}
          </div>

          <div style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 22, margin: 0 }}>Signalements à traiter</h3>
              <Link to="/admin/signalements" className="pill-btn">Voir tous</Link>
            </div>
            {data.recentSignalements.length === 0 && <p style={{ color: 'var(--char-500)', fontStyle: 'italic', fontSize: 13 }}>Aucun nouveau signalement. ✓</p>}
            {data.recentSignalements.map((s) => (
              <div key={s.id} style={{
                padding: '12px 0', borderBottom: '1px solid var(--line)',
                display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ fontWeight: 500, fontSize: 14, display: 'block' }}>{s.title}</b>
                  <small style={{ display: 'block', marginTop: 3, fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--char-500)' }}>
                    {s.reference}{s.location ? ` · 📍 ${s.location}` : ''}
                  </small>
                </div>
                <span className={`status-pill ${s.priority === 'urgent' ? 'err' : 'warn'}`}>
                  {s.priority === 'urgent' ? 'Urgent' : 'Nouveau'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

/* ============ Sub-components ============ */
const KpiCard = ({ label, value, unit = '', sub, color, dark, to }) => {
  const inner = (
    <div className={`kpi${dark ? ' dark' : ''}`} style={{ cursor: to ? 'pointer' : 'default' }}>
      <span className="lab">
        <span className="dot" style={{ background: color }} />
        {label}
      </span>
      <b>{value}{unit && <span className="unit">{unit}</span>}</b>
      {sub && <span className="delta">{sub}</span>}
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>{inner}</Link> : inner;
};

const MiniKpi = ({ label, value, to }) => (
  <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
    <div
      style={{
        background: 'var(--bone)', border: '1px solid var(--line)',
        padding: '16px 18px',
        transition: 'transform .2s, border-color .2s',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--char-900)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line)'; e.currentTarget.style.transform = 'none'; }}
    >
      <span style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--char-500)', display: 'block', marginBottom: 8 }}>
        {label}
      </span>
      <b style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 32, lineHeight: 1, letterSpacing: '-.015em', display: 'block', color: 'var(--char-900)' }}>
        {value}
      </b>
    </div>
  </Link>
);

export default Dashboard;
